import { FC } from "react";
import { Home, BarChart3, Users, Handshake, Package, Ticket, Gift } from "lucide-react";
import { useAppSelector, useAppDispatch } from "../../hooks/redux";
import { navigateTo, setActiveTab, Screen } from "../../store/slices/navigationSlice";
import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { cn } from "@/lib/utils";
import { useLanguage } from "../../contexts/LanguageContext";

type ProductCategory = 'products' | 'incentives' | 'promotions';

interface NavItem {
  id: Screen;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  emoji?: string;
}

const navItems: NavItem[] = [
  {
    id: "dashboard",
    labelKey: "navigation.home",
    icon: Home,
  },
  {
    id: "interactions",
    labelKey: "navigation.interactions",
    icon: Handshake,
  },
  {
    id: "contextSales",
    labelKey: "navigation.all_customers",
    icon: Users,
  },
  {
    id: "products",
    labelKey: "navigation.products",
    icon: Package,
  },
  {
    id: "reports",
    labelKey: "navigation.reports",
    icon: BarChart3,
  },
];

const productMenuItems = [
  {
    value: 'products' as ProductCategory,
    labelKey: 'products.tabs.products',
    fallbackLabel: 'Produk',
    icon: Package,
  },
  {
    value: 'incentives' as ProductCategory,
    labelKey: 'products.tabs.incentives',
    fallbackLabel: 'Kupon',
    icon: Ticket,
  },
  {
    value: 'promotions' as ProductCategory,
    labelKey: 'products.tabs.promotions',
    fallbackLabel: 'Promosi',
    icon: Gift,
  },
];

export const BottomNavigation: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useLanguage();
  const currentScreen = useAppSelector(
    (state) => state.navigation.currentScreen,
  );
  const activeTab = useAppSelector(
    (state) => state.navigation.activeTab,
  );

  const handleNavigation = (screenId: Screen) => {
    dispatch(navigateTo(screenId));
  };

  const handleProductsSelect = (tab: ProductCategory) => {
    dispatch(setActiveTab(tab));
    dispatch(navigateTo('products'));
  };

  // Render nav button content
  const renderNavButtonContent = (item: NavItem, isActive: boolean) => {
    const IconComponent = item.icon;
    return (
      <div className="flex flex-col items-center justify-center gap-0.5 w-full">
        <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
        <span
          className={cn(
            "text-[9px] sm:text-[10px] leading-[1.2] text-center w-full",
            isActive ? "font-medium" : "font-normal"
          )}
          style={{
            wordBreak: 'normal',
            overflowWrap: 'break-word',
            hyphens: 'none',
            whiteSpace: 'normal'
          }}
        >
          {t(item.labelKey)}
        </span>
        {/* Active indicator */}
        {isActive && (
          <div className="w-1 h-1 bg-primary rounded-full flex-shrink-0" />
        )}
      </div>
    );
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background safe-area-bottom"
      data-testid="bottom-navigation"
    >
      {/* Safe area padding for devices with home indicators */}
      <div className="px-1 pt-2 pb-1 sm:px-2 sm:pb-2">
        <div className="flex justify-around items-end gap-0.5">
          {navItems.map((item) => {
            const isActive = currentScreen === item.id;

            // Special handling for Products - use DropdownMenu
            if (item.id === 'products') {
              return (
                <DropdownMenu key={item.id}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "flex flex-col items-center justify-center flex-1 max-w-[70px] sm:max-w-[90px] min-h-[56px] py-1 sm:py-1.5 px-0.5 sm:px-1 transition-colors",
                        isActive
                          ? "text-primary bg-primary/10"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent"
                      )}
                    >
                      {renderNavButtonContent(item, isActive)}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="top"
                    align="center"
                    sideOffset={8}
                    className="w-48"
                  >
                    {productMenuItems.map((menuItem) => {
                      const IconComponent = menuItem.icon;
                      const isSelected = activeTab === menuItem.value;
                      return (
                        <DropdownMenuItem
                          key={menuItem.value}
                          onClick={() => handleProductsSelect(menuItem.value)}
                          className={cn(
                            "flex items-center gap-3 py-3 px-3 cursor-pointer",
                            isSelected && "bg-primary/10 text-primary"
                          )}
                        >
                          <IconComponent className="w-5 h-5" />
                          <span className="font-medium">
                            {t(menuItem.labelKey) || menuItem.fallbackLabel}
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }

            // Regular nav items
            return (
              <Button
                key={item.id}
                variant="ghost"
                size="sm"
                onClick={() => handleNavigation(item.id)}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 max-w-[70px] sm:max-w-[90px] min-h-[56px] py-1 sm:py-1.5 px-0.5 sm:px-1 transition-colors",
                  isActive
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )}
              >
                {renderNavButtonContent(item, isActive)}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
